import { Euler, Matrix4, Vector3 } from 'three';

import { PlatePair } from '@paragon/contract-library';
import { convertDegreesToRadians } from '@paragon/geometry-library';
import { Point3D } from '@paragon/three-shared';
import { sortBy } from '@paragon/utilities';

import { AngleDimensionProps } from './AngleDimension';

export const getAngleDimensionPropsForPlatePair = (platePair: PlatePair): AngleDimensionProps => {
  const euler = {
    x: convertDegreesToRadians(platePair.angleX),
    y: convertDegreesToRadians(platePair.angleY),
    z: convertDegreesToRadians(platePair.angleZ),
  };
  const position = {
    x: platePair.centerX,
    y: platePair.centerY,
    z: platePair.centerZ,
  };
  const { width, length } = platePair;

  const leftBottomPointLocal = new Vector3(0, 0);
  const leftTopPointLocal = new Vector3(0, width);
  const rightBottomPointLocal = new Vector3(length, 0);
  const rightTopPointLocal = new Vector3(length, width);

  const matrix = getPlatePairTransformationMatrix(euler, position, length, width);

  const leftBottomPointWorld = new Vector3().copy(leftBottomPointLocal).applyMatrix4(matrix);
  const leftTopPointWorld = new Vector3().copy(leftTopPointLocal).applyMatrix4(matrix);
  const rightBottomPointWorld = new Vector3().copy(rightBottomPointLocal).applyMatrix4(matrix);
  const rightTopPointWorld = new Vector3().copy(rightTopPointLocal).applyMatrix4(matrix);

  const sortedPoints = sortBy(
    [leftBottomPointWorld, leftTopPointWorld, rightBottomPointWorld, rightTopPointWorld],
    (vector) => vector.y,
    'desc',
  );

  const basePoint = sortedPoints[0];
  const endLineEndPoint = sortedPoints[1];
  const baseLineEndPoint = { x: endLineEndPoint.x, y: basePoint.y, z: endLineEndPoint.z };

  return {
    basePoint,
    baseLineEndPoint,
    endLineEndPoint,
  };
};

function getPlatePairTransformationMatrix(euler: Point3D, position: Point3D, length: number, width: number): Matrix4 {
  const eulerRotation = new Euler(euler.x, euler.y, euler.z);
  return new Matrix4()
    .makeTranslation(position.x, position.y, position.z)
    .multiply(new Matrix4().makeRotationFromEuler(eulerRotation))
    .multiply(new Matrix4().makeTranslation(-length / 2, -width / 2, 0));
}
